/**
 * Funil de candidatura a consultor (PDF do Consultor, Etapas 1 a 3).
 *
 * Espelha `StatusCandidatura` do backend. A máquina inteira é declarada porque é a
 * especificação; as telas de hoje só operam até APROVADA/REPROVADA.
 */
export type StatusCandidatura =
  // Etapa 1 — interesse e avaliação
  | 'RECEBIDA'
  | 'EM_AVALIACAO'
  | 'APROVADA'
  | 'REPROVADA'
  // Etapa 2 — convite e cadastro
  | 'CONVITE_ENVIADO'
  | 'CADASTRO_INICIADO'
  | 'CONVITE_EXPIRADO'
  // Etapa 3 — validação e liberação
  | 'CADASTRO_EM_ANALISE'
  | 'PENDENCIA_CADASTRAL'
  | 'ATIVO'
  | 'BLOQUEADO'
  | 'INATIVO'

export const ROTULO_STATUS_CANDIDATURA: Record<StatusCandidatura, string> = {
  RECEBIDA: 'Recebida',
  EM_AVALIACAO: 'Em avaliação',
  APROVADA: 'Aprovada',
  REPROVADA: 'Reprovada',
  CONVITE_ENVIADO: 'Convite enviado',
  CADASTRO_INICIADO: 'Cadastro iniciado',
  CONVITE_EXPIRADO: 'Convite expirado',
  CADASTRO_EM_ANALISE: 'Cadastro em análise',
  PENDENCIA_CADASTRAL: 'Pendência cadastral',
  ATIVO: 'Ativo',
  BLOQUEADO: 'Bloqueado',
  INATIVO: 'Inativo',
}

export const COR_STATUS_CANDIDATURA: Record<
  StatusCandidatura,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  RECEBIDA: 'info',
  EM_AVALIACAO: 'warning',
  APROVADA: 'success',
  REPROVADA: 'error',
  CONVITE_ENVIADO: 'info',
  CADASTRO_INICIADO: 'info',
  CONVITE_EXPIRADO: 'error',
  CADASTRO_EM_ANALISE: 'warning',
  PENDENCIA_CADASTRAL: 'warning',
  ATIVO: 'success',
  BLOQUEADO: 'error',
  INATIVO: 'default',
}

export interface Candidatura {
  id: string
  nome: string
  email: string
  telefone: string
  resumo?: string | null
  status: StatusCandidatura
  /** Transições válidas a partir do status atual — vêm do backend, não replicadas aqui. */
  proximosStatus: StatusCandidatura[]
  parecer?: string | null
  avaliadoPor?: string | null
  avaliadoEm?: string | null
  createdAt: string
  updatedAt: string
}

export interface CandidaturaRequest {
  nome: string
  email: string
  telefone: string
  resumo?: string
  captchaToken?: string
}

export interface AvaliarCandidaturaRequest {
  novoStatus: StatusCandidatura
  parecer?: string
}

/** Convite recém-emitido. O token só aparece aqui — não há como recuperá-lo depois. */
export interface ConviteEmitido {
  conviteId: string
  candidaturaId: string
  token: string
  /** Caminho relativo; o portal monta a URL absoluta com o próprio host. */
  link: string
  expiraEm: string
  /** O SMTP aceitou o e-mail com o link? Se não, o operador envia o link à mão. */
  emailEnviado: boolean
  emailDestinatario?: string
}

/** O que a tela de cadastro recebe ao abrir um convite válido. */
export interface ConvitePrefill {
  nome: string
  email: string
}

/** Uma passagem profissional (PDF, Etapa 2 — histórico consolidado). */
export interface ExperienciaRequest {
  empresa: string
  cargo: string
  periodo?: string
  atividades?: string
  segmentos?: string
}

/**
 * Cadastro completo por convite.
 *
 * Não tem e-mail: vem da candidatura, porque o convite é nominal. E não tem
 * "Modalidade de Trabalho", "Raio de Atuação" nem "Especialidade" — o PDF questiona
 * os três e pede a remoção na falta de uso prático.
 */
export interface CadastroConsultorRequest {
  nomeCompleto: string
  cpf: string
  dataNascimento: string
  celular: string
  telefone?: string
  senha: string

  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade: string
  uf: string

  banco?: string
  agencia?: string
  conta?: string
  tipoConta?: string
  chavePix?: string
  cnpj?: string

  formacao?: string
  segmentosExperiencia?: string
  mercadoOrigem?: string
  tiposEmpresaAtendidos?: string
  produtosComercializados?: string
  principalRelacionamento?: string
  regiaoAtuacao?: string
  resumoProfissional?: string
  experiencias?: ExperienciaRequest[]

  aceiteTermos: boolean
  versaoTermos?: string
}

/** Uma passagem profissional, como o back-office a lê. */
export interface ExperienciaResumo {
  empresa: string
  cargo: string
  periodo?: string | null
  atividades?: string | null
  segmentos?: string | null
}

/**
 * Cadastro submetido, para a validação da Etapa 3.
 *
 * Não traz senha nem CPF: a senha nunca sai do Keycloak, e o CPF é dado sensível que a
 * fila de validação não precisa exibir.
 */
export interface CandidaturaCadastro {
  id: string
  nome: string
  email: string
  telefone: string
  status: StatusCandidatura
  proximosStatus: StatusCandidatura[]

  formacao?: string | null
  segmentosExperiencia?: string | null
  mercadoOrigem?: string | null
  tiposEmpresaAtendidos?: string | null
  produtosComercializados?: string | null
  principalRelacionamento?: string | null
  regiaoAtuacao?: string | null
  resumoProfissional?: string | null
  experiencias: ExperienciaResumo[]

  aceiteTermosEm?: string | null
  aceiteTermosVersao?: string | null
  tenantId?: string | null
  /** Falso enquanto o convite não foi usado — sem conta, não há acesso a liberar. */
  possuiUsuario: boolean

  parecer?: string | null
  createdAt: string
  updatedAt: string
}

export interface AprovarCadastroRequest {
  tenantId: string
  tipoCanal?: string
  parecer?: string
}
